import Accessor from '@arcgis/core/core/Accessor';
import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';
import { whenDefinedOnce, whenFalseOnce, whenTrueOnce } from '@arcgis/core/core/watchUtils';
import MapView from "@arcgis/core/views/MapView";
import SceneView from "@arcgis/core/views/SceneView";
import Map from "@arcgis/core/Map";

import MyAppsViewModel from '../../data/MyApps/MyAppsViewModel';
import ItemLoaderViewModel from '../../data/ItemLoader/ItemLoaderViewModel';
import AppTypeInfoViewModel from '../../data/AppTypeInfo/AppTypeInfoViewModel';
import PublishAppViewModel from '../../data/PublishApp/PublishAppViewModel';

import { convertNotCompatToApps, EAppTypeIdentifier, EWebmapNotCompatible, getNotCompats_Specific, performWebmapChecks } from '../../utils/Compatibility/webmapChecks';
import { AppTypeTileProperties } from 'src/app/widgets/AppTypeTile';

import appMessagesT9N from "../../../t9n/App/App.json";
import { findAppTypeBadge } from '../../utils/appUtils';

import Telemetry from "@esri/telemetry";
import NavBarViewModel from '../NavBar/NavBarViewModel';
import AlertWrapper from '../../widgets/AlertWrapper';
import Collection from '@arcgis/core/core/Collection';

const FEATURED_TYPE_KEYWORD = "InstantAppGallery:Featured";


@subclass('Landing.ui.ContentArea.ContentAreaViewModel')
export default class ContentAreaViewModel extends Accessor {
	@property()
	view: __esri.MapView | __esri.SceneView;

	@property()
	portal: __esri.Portal;

	@property()
	telemetry: Telemetry;

	@property()
	itemLoaderVM: ItemLoaderViewModel = new ItemLoaderViewModel();

	@property()
	myAppsDataVM: MyAppsViewModel = new MyAppsViewModel();

	@property()
	appTypeInfoVM: AppTypeInfoViewModel = new AppTypeInfoViewModel();

	@property()
	publishAppVM: PublishAppViewModel = new PublishAppViewModel();

	@property()
	notCompatible: EWebmapNotCompatible[];

	@property()
	notCompatibleAppTypes: EAppTypeIdentifier[];

	@property()
	appMessages: typeof appMessagesT9N;

	@property()
	activeAlerts: __esri.Collection<AlertWrapper> = new Collection();

	@property()
	navbarVM: NavBarViewModel;

	constructor(params?: any) {
		super(params);

		// Populate DataVMs with portal
		whenDefinedOnce(this, "portal", () => {
			this.itemLoaderVM.portal = this.portal;
			this.myAppsDataVM.portal = this.portal;
			this.appTypeInfoVM.portal = this.portal;
			this.publishAppVM.portal = this.portal;

			this.publishAppVM.navbarVM = this.navbarVM;
			this.publishAppVM.telemetry = this.telemetry;
		});
	}

	createMap(dom: HTMLDivElement): void {
		whenTrueOnce(this.itemLoaderVM, "isItemLoaderDone", () => {
			switch (this.itemLoaderVM.loadedItemType) {
				case "webmap":
					this._handleWebmap(dom);
					break;
				// case "scene":
				// case "group":
				case "none":
					this._handleNoLoadedItem(dom);
					break;
			}
		});
	}

	/**
	 * Takes in the PortalItem for the AppType and formats the data for ease of use
	 * @param item - PortalItem representing the AppType (ex: Item for Attachment Viewer, Item for Media... etc)  
	 */
	formatAppTypeItems(item: __esri.PortalItem): AppTypeTileProperties {
		return {
			webmapItem: this?.itemLoaderVM?.webmap?.portalItem != null ? this?.itemLoaderVM?.webmap?.portalItem : null,
			publishAppVM: this.publishAppVM,
			appTypeInfo: {
				screenshotUrl: item.screenshots[0] != null
					? `${this.publishAppVM.orgUrl}/sharing/rest/content/items/${item.id}/info/${item.screenshots[0]}`
					: item.thumbnailUrl, // fall back to thumbnail if scrshot undefined
				thumbnailUrl: item.thumbnailUrl,
				created: item.created,
				badge: findAppTypeBadge(item),
				appTypeTitle: this._titleFilter(item.title),
				appTypeUrlFragment: item.url as EAppTypeIdentifier,
				appTypeUrl: `${this.publishAppVM.orgUrl}${item.url}`.replace("http://", "https://"),
				compatibleMsgs: this.notCompatible != null ? getNotCompats_Specific(item.url as any, this.notCompatible) : null,
				appTypeId: item.id,
				portalItem: item
			}
		};
	}

	filterFeaturedApps(item: __esri.PortalItem): boolean {
		const index = item.typeKeywords.findIndex((keyword: string) => {
			return keyword === FEATURED_TYPE_KEYWORD;
		});

		return index !== -1;
	}

	createView(originalData: unknown[]): unknown[] {
		if(originalData != null){
			return [...originalData];
		}else{
			return [];
		}
	}

	/** Configure Alert Notification properly */
	showAlert(title: string, msg: string, type: "success" | "error", goToLinkId?: string) {

		const alert = new AlertWrapper({
			title,
			msg,
			type,
			goToLinkId
		});

		this.activeAlerts.add(alert);

		whenTrueOnce(alert, "isDestroyed", (isDestroyed: boolean) => {
			if (isDestroyed) { this.activeAlerts.remove(alert); }
		});
	}

	/**
	 * Disables all zoom gestures on the given view instance.
	 *
	 * @param {esri/views/MapView} view - The MapView instance on which to
	 *                                  disable zooming gestures.
	 */
	private _disableZooming(view) {
		view.popup.dockEnabled = true;

		// Removes the zoom action on the popup
		view.popup.actions = [];

		// stops propagation of default behavior when an event fires
		function stopEvtPropagation(event) {
			event.stopPropagation();
		}

		// disable mouse wheel scroll zooming on the view
		view.on("mouse-wheel", stopEvtPropagation);

		// disable zooming via double-click on the view
		view.on("double-click", stopEvtPropagation);

		// disable zooming out via double-click + Control on the view
		view.on("double-click", ["Control"], stopEvtPropagation);

		// disables pinch-zoom and panning on the view
		view.on("drag", stopEvtPropagation);

		// disable the view's zoom box to prevent the Shift + drag
		// and Shift + Control + drag zoom gestures.
		view.on("drag", ["Shift"], stopEvtPropagation);
		view.on("drag", ["Shift", "Control"], stopEvtPropagation);

		// prevents zooming with the + and - keys
		view.on("key-down", function (event) {
			const prohibitedKeys = [
				"+",
				"-",
				"Shift",
				"_",
				"=",
				"ArrowUp",
				"ArrowDown",
				"ArrowRight",
				"ArrowLeft"
			];
			const keyPressed = event.key;
			if (prohibitedKeys.indexOf(keyPressed) !== -1) {
				event.stopPropagation();
			}
		});

		return view;
	}

	/** Filter strings out of Title - for example: "(beta)" */
	private _titleFilter(title: string): string {
		return title.replace(`(${this.appMessages.appBadges.Beta.toLocaleLowerCase()})`, ``);
	}

	private _handleWebmap(dom: HTMLDivElement): void {
		const webmap = this.itemLoaderVM.webmap;

		this.view = new MapView({
			container: dom,
			map: webmap
		});
		const { view } = this;
		view.ui.components = ["attribution"];

		view.when(() => {
			view.goTo(view.extent);
			this._disableZooming(view);
		});

		whenTrueOnce(webmap, "loaded", async () => {
			this.notCompatible = await performWebmapChecks(webmap);
			this.notCompatibleAppTypes = convertNotCompatToApps(this.notCompatible);
		});
	}

	private _handleNoLoadedItem(dom: HTMLDivElement): void {
		const map = new Map({
			basemap: this.portal.isPortal ? this.portal.defaultBasemap : "satellite"			
		});

		this.view = new SceneView({
			container: dom,
			map: map,
			scale: 500000000,
			camera: {
				position: [0, 0, 40000000] as any,
				heading: -23.5
			}
		});
		this.view.ui.components = ["attribution"];

		// *** Disabling rotation due to high cpu usage ***
		// this.view.when(() => {
		// 	whenFalseOnce(this.view, "updating", this._rotate.bind(this));
		// });
	}

	private _rotate() {
		const camera = (this.view as __esri.SceneView).camera.clone();
		camera.position.longitude -= 0.05;
		this.view.goTo(camera, { animate: false });
		requestAnimationFrame(this._rotate.bind(this));
	}

}



